<script setup lang="ts">
	/**
	 * Renglón siempre visible bajo la barra de resultados, aunque el panel de filtros
	 * esté cerrado:
	 *
	 * - lo que está filtrando ahora, cada chip con su ✕;
	 * - filtros rápidos (+) armados con lo que trajo la búsqueda, para aplicar lo más
	 *   común sin abrir el panel. Uno ya aplicado deja de sugerirse.
	 */
	import { useHotelsStore } from '@/stores/hotels'
	import { storeToRefs } from 'pinia'
	import { computed } from 'vue'

	import CheapestRateNotice from './CheapestRateNotice.vue'

	interface Chip {
		id: string
		label: string
		action: () => void
	}

	const store = useHotelsStore()
	const {
		minRatingFilter,
		minPriceFilter,
		maxPriceFilter,
		accommodationTypeFilter,
		boardFilter,
		chainFilter,
		cityFilter,
		areaFilter,
		cancellationFilter,
		boardOptions,
		chainOptions,
		cityOptions,
		areaOptions,
		cancellationCounts,
		activeFilterCount,
		hotels,
	} = storeToRefs(store)
	const { resetResultFilters } = store

	const QUICK_CHAINS = 2
	const QUICK_CITIES = 2
	const QUICK_AREAS = 2

	const currency = computed(() => hotels.value[0]?.requestedCurrency || 'MXN')

	function formatPrice(value: number) {
		return new Intl.NumberFormat('es-MX').format(value)
	}

	function without(list: string[], key: string) {
		return list.filter((value) => value !== key)
	}

	const appliedChips = computed<Chip[]>(() => {
		const chips: Chip[] = []

		if (cancellationFilter.value !== 'all') {
			chips.push({
				id: 'cancellation',
				label:
					cancellationFilter.value === 'refundable' ?
						'Cancelación sin costo'
					:	'No reembolsable',
				action: () => (cancellationFilter.value = 'all'),
			})
		}
		if (minRatingFilter.value > 0) {
			chips.push({
				id: 'rating',
				label: `${minRatingFilter.value}★ o más`,
				action: () => (minRatingFilter.value = 0),
			})
		}
		if (minPriceFilter.value !== null || maxPriceFilter.value !== null) {
			const min = minPriceFilter.value
			const max = maxPriceFilter.value
			const range =
				min !== null && max !== null ?
					`${formatPrice(min)} – ${formatPrice(max)}`
				: min !== null ? `Desde ${formatPrice(min)}`
				: `Hasta ${formatPrice(max ?? 0)}`
			chips.push({
				id: 'price',
				label: `${range} ${currency.value} por noche`,
				action: () => {
					minPriceFilter.value = null
					maxPriceFilter.value = null
				},
			})
		}
		for (const key of chainFilter.value) {
			chips.push({
				id: `chain-${key}`,
				label:
					chainOptions.value.find((option) => option.key === key)?.label ?? key,
				action: () => (chainFilter.value = without(chainFilter.value, key)),
			})
		}
		for (const key of cityFilter.value) {
			chips.push({
				id: `city-${key}`,
				label:
					cityOptions.value.find((option) => option.key === key)?.label ?? key,
				action: () => (cityFilter.value = without(cityFilter.value, key)),
			})
		}
		for (const key of areaFilter.value) {
			chips.push({
				id: `area-${key}`,
				label:
					areaOptions.value.find((option) => option.key === key)?.label ?? key,
				action: () => (areaFilter.value = without(areaFilter.value, key)),
			})
		}
		if (accommodationTypeFilter.value !== 'all') {
			chips.push({
				id: 'type',
				label: accommodationTypeFilter.value,
				action: () => (accommodationTypeFilter.value = 'all'),
			})
		}
		if (boardFilter.value !== 'all') {
			chips.push({
				id: 'board',
				label: boardFilter.value,
				action: () => (boardFilter.value = 'all'),
			})
		}

		return chips
	})

	const quickChips = computed<Chip[]>(() => {
		if (hotels.value.length === 0) return []

		const chips: Chip[] = []

		if (
			cancellationFilter.value === 'all' &&
			cancellationCounts.value.refundable > 0
		) {
			chips.push({
				id: 'quick-refundable',
				label: 'Cancelación sin costo',
				action: () => (cancellationFilter.value = 'refundable'),
			})
		}
		if (minRatingFilter.value === 0) {
			chips.push({
				id: 'quick-rating',
				label: '4★ o más',
				action: () => (minRatingFilter.value = 4),
			})
		}

		// El régimen sólo lo manda Hotelbeds: en un resultado con hoteles sin `board`
		// (Amadeus) el atajo los escondería aunque su tarifa incluya desayuno, así que
		// sólo se ofrece cuando todos lo traen, y se rotula con el régimen exacto.
		const breakfast = boardOptions.value.find((board) =>
			/desayuno|breakfast/i.test(board)
		)
		if (
			breakfast &&
			boardFilter.value === 'all' &&
			hotels.value.every((hotel) => !!hotel.board)
		) {
			chips.push({
				id: 'quick-breakfast',
				label: `Régimen: ${breakfast}`,
				action: () => (boardFilter.value = breakfast),
			})
		}

		// Las cadenas, municipios y zonas con más hoteles en este resultado; si sólo hay
		// uno, filtrar por él no cambia nada y no se sugiere.
		const popular = (
			options: typeof chainOptions.value,
			selected: string[],
			limit: number
		) =>
			options.length > 1 ?
				options
					.filter((option) => !selected.includes(option.key))
					.slice(0, limit)
			:	[]

		for (const option of popular(
			chainOptions.value,
			chainFilter.value,
			QUICK_CHAINS
		)) {
			chips.push({
				id: `quick-chain-${option.key}`,
				label: option.label,
				action: () => (chainFilter.value = [...chainFilter.value, option.key]),
			})
		}
		for (const option of popular(
			cityOptions.value,
			cityFilter.value,
			QUICK_CITIES
		)) {
			chips.push({
				id: `quick-city-${option.key}`,
				label: option.label,
				action: () => (cityFilter.value = [...cityFilter.value, option.key]),
			})
		}
		for (const option of popular(
			areaOptions.value,
			areaFilter.value,
			QUICK_AREAS
		)) {
			chips.push({
				id: `quick-area-${option.key}`,
				label: option.label,
				action: () => (areaFilter.value = [...areaFilter.value, option.key]),
			})
		}

		return chips
	})
</script>

<template>
	<div
		v-if="appliedChips.length > 0 || quickChips.length > 0"
		class="flex flex-col gap-2"
	>
		<div class="flex flex-wrap items-center gap-1.5">
			<span
				v-for="chip in appliedChips"
				:key="chip.id"
				class="inline-flex max-w-full items-center gap-1 rounded-full bg-[#021F58] py-1 pl-3 pr-1 text-[12px] font-medium text-white"
			>
				<span class="truncate">{{ chip.label }}</span>
				<button
					type="button"
					class="grid size-5 shrink-0 place-items-center rounded-full text-white/70 hover:bg-white/15 hover:text-white"
					:aria-label="`Quitar filtro ${chip.label}`"
					@click="chip.action()"
				>
					<span
						class="material-symbols-outlined text-[14px] leading-none"
						aria-hidden="true"
						>close</span
					>
				</button>
			</span>

			<span
				v-if="appliedChips.length > 0 && quickChips.length > 0"
				class="mx-1 h-4 w-px bg-gray-200"
				aria-hidden="true"
			></span>

			<button
				v-for="chip in quickChips"
				:key="chip.id"
				type="button"
				class="inline-flex max-w-full items-center gap-1 rounded-full bg-white py-1 pl-2 pr-3 text-[12px] font-medium text-gray-700 ring-1 ring-inset ring-gray-300 transition-colors hover:bg-gray-50 hover:ring-gray-400"
				:aria-label="`Filtrar por ${chip.label}`"
				@click="chip.action()"
			>
				<span
					class="material-symbols-outlined text-[14px] leading-none text-gray-400"
					aria-hidden="true"
					>add</span
				>
				<span class="truncate">{{ chip.label }}</span>
			</button>

			<button
				v-if="activeFilterCount > 0"
				type="button"
				class="ml-auto px-1 text-[12px] font-medium text-[#021F58]/70 transition-colors hover:text-[#021F58]"
				@click="resetResultFilters"
			>
				Limpiar todo
			</button>
		</div>

		<!-- Con un filtro de cancelación activo el aviso se lee sin abrir el panel. -->
		<CheapestRateNotice v-if="cancellationFilter !== 'all'" />
	</div>
</template>
